// @flow
import invariant from 'invariant';
import {
  filterNulls,
  mapObject,
  values,
  arrToObject,
  updateObject,
  zip,
  complement,
  objectEmpty,
  setEquals,
  isObject,
  remove
} from 'utils/functional';

import { NO_FILTER_SENTINEL } from 'utils/constants';

type AllSearchTypes = StaffTypeFilter | ListingType | 'CMS_PAGE';

export const searchableFacetsForType = (
  type: AllSearchTypes
): Array<string> => {
  const byType: { [AllSearchTypes]: Array<string> } = {
    ALL: [],
    JOB: ['orgAreasOfFocusFacet', 'jobKeywordsFacet'],
    INTERNSHIP: ['orgAreasOfFocusFacet', 'internshipKeywordsFacet'],
    VOLOP: ['volopAreasOfFocusFacet', 'volopKeywordsFacet'],
    ORG: ['orgAreasOfFocusFacet'],
    IDEALIST_DAY_EVENT: ['idealistDayEventAreasOfFocusFacet']
  };

  return byType[type] || [];
};

export const sideFacetsForType = (
  searchFacets: $ReadOnly<{ [string]: SearchFacet }>,
  type: AllSearchTypes
): Array<SearchFacet> => {
  const facetTypeToKeys: { [AllSearchTypes]: Array<string> } = {
    ALL: [],
    JOB: [
      'listingLocationTypeFacet',
      'jobKeywordsFacet',
      'orgAreasOfFocusFacet',
      'educationFacet',
      'professionalLevelFacet',
      'employmentTypeFacet',
      'orgTypeFacet',
      'statusFacet',
      'localeFacet'
    ],
    INTERNSHIP: [
      'listingLocationTypeFacet',
      'internshipKeywordsFacet',
      'orgAreasOfFocusFacet',
      'orgTypeFacetNoCorp',
      'paidFacet',
      'paymentFacet',
      'statusFacet',
      'localeFacet'
    ],
    ORG: ['orgAreasOfFocusFacet', 'orgTypeFacetNoCorp', 'statusFacet'],
    GROUP: ['statusFacet', 'groupAreasOfFocusFacet'],
    IDEALIST_DAY_EVENT: [
      'idealistDayFacet',
      'statusFacet',
      'localeFacet',
      'idealistDayEventAreasOfFocusFacet'
    ],
    // Anything added for actions must also be added in VolopSearchFiltersSidebarContainer.
    VOLOP: [
      'volopAreasOfFocusFacet',
      'volopKeywordsFacet',
      'listingLocationTypeFacet',
      'isCovidFacet',
      'hasLocationFacet',
      'actionTypeFacet',
      'actionDateFacet',
      'volopSourceFacet', // Not shown in sidebar.
      'localeFacet'
    ],
    GRAD_PROGRAM: ['publicPrivateFacet', 'statusFacet', 'gradAreasOfStudyFacet']
  };
  const facetKeys = facetTypeToKeys[type] || [];

  const includedFilters = facetKeys
    .filter(key => searchFacets[key])
    .map(key => searchFacets[key]);

  return includedFilters;
};

const semanticToSavedSearch = (s: SemanticFilter) => {
  const { name, operator, value } = s;
  const operatorStr = operator === '>' ? 'GT' : 'LT';
  return { [`${name}${operatorStr}`]: value };
};

// stateSearchFilters takes the redux filter state and
// outputs an "almost" saved-search-serializable data structure
export function stateSearchFilters(
  searchFacets: { [string]: SearchFacet },
  facetFilters: SearchFilters | SearchFiltersWithType<AllSearchTypes>
) {
  let filters = {};

  if (facetFilters && facetFilters.type !== 'ALL') {
    filters.type = facetFilters.type;
  }

  values(searchFacets).forEach(facet => {
    const setting = facetFilters[facet.id];

    if (facet.type === 'boolean') {
      if (setting === 'YES') filters[facet.id] = true;
      else if (setting === 'NO') filters[facet.id] = false;
      // take no action for 'ALL'
    } else if (facet.type === 'array' || facet.type === 'string') {
      // Check if the array has all options in it aswell
      if (!setting || setting.length === 0) return;

      const allOptionsSelected = facet.options.every(
        i => Array.isArray(setting) && setting.includes(i)
      );
      if (!allOptionsSelected) filters[facet.id] = setting;
    } else if (facet.type === 'date') {
      const s = ((setting: any): { [string]: SemanticFilter });
      if (!objectEmpty(s)) {
        filters = {
          ...filters,
          ...updateObject(
            ...values(s)
              .filter(complement(objectEmpty))
              .map(semanticToSavedSearch)
          )
        };
      }
    } else {
      throw new Error(`invalid facet type: ${facet.type}`);
    }
  });

  return filters;
}

// serializeFiltersForSavedSearch takes the redux filter state and outputs
// a saved-search-serializable data structure. See `stateSearchFilters`
export const serializeFiltersForSavedSearch = ({
  searchFacets,
  filters
}: {
  filters: SearchFilters | SearchFiltersWithType<AllSearchTypes>,
  searchFacets: { [string]: SearchFacet }
}) =>
  mapObject(stateSearchFilters(searchFacets, filters), (key, value) =>
    ['type', 'startsLT', 'endsGT'].includes(key)
      ? [key, value]
      : [searchFacets[key].name, value]
  );

// Parse a semantic filter encoded in a query param into a redux-friendly
// data structure
const parseSemantic = val => {
  if (!val) return null;
  const re = /NAME(.+)OP(.+)VAL(.+)/gi;
  const result = re.exec(val);
  return (
    result && {
      name: result[1],
      operator: result[2],
      value: result[3] && Number(result[3]),
      filterType: 'semantic'
    }
  );
};

// Convert a redux-friendly semantic filter into a URL query param
export const urlStringSemantic = (s: SemanticFilter) =>
  `NAME${s.name}OP${s.operator}VAL${s.value}`;

export const areFiltersDefault = (
  facets: Array<SearchFacet>,
  facetsThatArentLoaded: Array<string>,
  filters: SearchFilters
) => {
  const facetsToCheck = remove(facets, f =>
    facetsThatArentLoaded.includes(f.id)
  );
  const arrayCheck = (facet, filter) =>
    !filter ||
    (Array.isArray(filter) && setEquals(facet.options, filter)) ||
    filter === 'ALL';
  const dateCheck = (facet, filter) =>
    !filter ||
    // isObject and Array.isArray checks done for flow
    ((isObject(filter) &&
      objectEmpty(!Array.isArray(filter) && filterNulls(filter))) ||
      (isObject(filter) &&
        !Array.isArray(filter) &&
        values(filter).every(objectEmpty)));
  const defaultCheck = (facet, filter) => !filter || filter === 'ALL';
  return facetsToCheck.every(f => {
    const filter = filters[f.id];
    return f.type === 'array'
      ? arrayCheck(f, filter)
      : f.type === 'date'
      ? dateCheck(f, filter)
      : defaultCheck(f, filter);
  });
};

export const initialFacetFilters = (
  facets: Array<SearchFacet>,
  facetsThatArentLoaded: Array<string>,
  queryParams: $ReadOnly<{ [string]: string }>
) =>
  updateObject(
    ...(facets || [])
      .filter(f => !facetsThatArentLoaded.includes(f.id) || queryParams[f.name])
      .map(facet => {
        if (facet.type === 'array') {
          const paramValue = queryParams[facet.name];
          return paramValue === NO_FILTER_SENTINEL
            ? { [facet.id]: [] }
            : !paramValue
            ? { [facet.id]: facet.options }
            : {
                [facet.id]: Array.isArray(paramValue)
                  ? paramValue
                  : [paramValue]
              };
        }
        // Date is stored 'semantically' in redux and the URL
        if (facet.type === 'date') {
          invariant(
            facet.names,
            'date facets need names (for start and end dates)'
          );
          const names = facet.names;
          const vs = names.map(n => queryParams[n]).map(parseSemantic);
          // Only keep keys with non-null values
          return {
            [facet.id]: filterNulls(arrToObject(zip(names, vs)))
          };
        }
        return { [facet.id]: queryParams[facet.name] || 'ALL' };
      })
  );

export const initialFacetFiltersForMainSearch = (
  searchFacets: { string: SearchFacet },
  facetsThatArentLoaded: Array<string>,
  queryParams: $ReadOnly<{
    actionType?: string,
    type: StaffTypeFilter | ListingType,
    saved?: string,
    ...
  }>,
  reset?: boolean
) => {
  const type = queryParams.type;
  invariant(type, `missing type: ${JSON.stringify(queryParams)}`);
  const f = initialFacetFilters(
    sideFacetsForType(searchFacets, type),
    facetsThatArentLoaded,
    queryParams
  );
  let hasRefreshed;
  // @NOTE - This is kind of a hack and using a deprecated API
  // Maybe we can reconsider how we set the "ALL" filter?
  if (
    typeof window !== 'undefined' &&
    window.performance &&
    performance.navigation.type === 1
  ) {
    hasRefreshed = true;
  }

  if (
    !reset &&
    !hasRefreshed &&
    !queryParams.saved &&
    type === 'VOLOP' &&
    !queryParams.actionType
  ) {
    f.actionTypeFacet = ['VOLOP'];
  }
  return {
    type,
    ...f
  };
};
